import React from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight, Wallet, GitBranch, LineChart
} from "lucide-react";
import { CRM_IMAGE } from "../lib/constants";

const MODULES = [
  {
    icon: Wallet,
    title: "Client portal",
    desc: "Onboarding, funding, and wallet activity in one place",
  },
  {
    icon: GitBranch,
    title: "Partner hierarchy",
    desc: "Interactive IB tree with automated payout logic",
  },
  {
    icon: LineChart,
    title: "Risk console",
    desc: "Live exposure, limits, and profit-and-loss views",
  },
];

export default function CRMHighlight() {
  return (
    <section
      id="fx"
      data-testid="crm-highlight"
      className="relative theme-section py-24 sm:py-32 overflow-hidden scroll-mt-28"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#9FE3FF]/50 to-transparent" />
      <div className="absolute -bottom-48 left-0 w-[420px] h-[420px] bg-[#003E8C] rounded-full blur-[200px] opacity-40" />

      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Header — matches About / Services editorial pattern */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-14 sm:mb-16"
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="w-12 h-px bg-[#9FE3FF]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#9FE3FF] font-bold">
              / Flagship CRM
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tighter">
            One CRM.
            <br />
            <span className="text-gradient-cyan italic">Every desk workflow.</span>
          </h2>
          <p className="text-gray-400 text-base leading-relaxed mt-6">
            Bring onboarding, wallets, partner networks, and risk controls into one live
            operations hub. Go live in weeks instead of quarters — and release capabilities rivals
            are still planning.
          </p>
          <a
            href="#contact"
            data-testid="crm-cta"
            className="btn-outline inline-flex items-center gap-2 mt-8 px-7 py-3.5 rounded-full font-semibold"
          >
            Explore our CRM
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </motion.div>

        {/* Image + module strip — stacked band, not side-by-side card */}
        <div className="grid grid-cols-12 gap-6 lg:gap-8 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="col-span-12 lg:col-span-8 relative"
          >
            <div className="absolute -left-3 top-8 bottom-8 w-1 bg-gradient-to-b from-[#00C2FF] via-[#9FE3FF] to-transparent hidden sm:block" />
            <div className="relative ml-0 sm:ml-6 rounded-2xl overflow-hidden border border-white/10 group">
              <img
                src={CRM_IMAGE}
                alt="CRM Dashboard"
                className="w-full h-[280px] sm:h-[380px] lg:h-[440px] object-cover object-left-top transition-transform duration-[1.4s] group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#000000]/80 via-transparent to-[#00C2FF]/10" />
              {/* Corner brackets — replaces floating ticker card */}
              <div className="absolute top-5 left-5 w-8 h-8 border-t-2 border-l-2 border-[#00C2FF]" />
              <div className="absolute bottom-5 right-5 w-8 h-8 border-b-2 border-r-2 border-[#9FE3FF]" />
              <div className="absolute bottom-6 left-6 sm:left-10">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gray-400">
                  Live workspace
                </div>
                <div className="font-display text-xl sm:text-2xl font-bold theme-heading mt-1">
                  Broker CRM
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="col-span-12 lg:col-span-4 flex flex-col gap-4"
          >
            {MODULES.map((m, i) => {
              const Icon = m.icon;
              return (
                <div
                  key={m.title}
                  data-testid={`crm-point-${i}`}
                  className="flex-1 feature-card rounded-2xl p-5 sm:p-6 group"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center border ${
                        i % 2 === 0
                          ? "bg-[#00C2FF]/10 border-[#00C2FF]/40 text-[#00C2FF]"
                          : "bg-[#9FE3FF]/10 border-[#9FE3FF]/40 text-[#9FE3FF]"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-base font-semibold theme-heading group-hover:text-[#9FE3FF] transition-colors">
                        {m.title}
                      </h3>
                      <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">{m.desc}</p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-gray-600 shrink-0 group-hover:text-[#00C2FF] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>

        {/* Metric strip — distinct from About stats overlay */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10 pt-8 border-t border-white/10 grid grid-cols-2 sm:grid-cols-4 gap-8"
        >
          {[
            { v: "4 wks", l: "Avg. go-live" },
            { v: "1", l: "Unified stack" },
            { v: "24/7", l: "Desk support" },
            { v: "∞", l: "IB depth" },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-display text-2xl sm:text-3xl font-extrabold text-gradient-cyan">
                {s.v}
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mt-1 font-mono">
                {s.l}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
